// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxDatabase } from 'rxdb'
import { QueryOperator } from '../operators/QueryOperator'
import { MutationOperator } from '../operators/MutationOperator'
import type { QueryOptions } from '../operators/types'
import type { RoleMembershipModel } from '../../models/RoleMembership'
import { createId } from '../../index'

/**
 * Filters for querying role memberships.
 */
export interface RoleMembershipFilters {
  /** Filter by owning organisation ID. */
  orgId?: string
  /** Filter by member user ID. */
  userId?: string
  /** Filter by assigned role ID. */
  roleId?: string
  /** Filter by lifecycle status (`'active'`, `'suspended'`, `'pending'`). */
  status?: string
  /** Filter by scope type (e.g. `'workspace'`, `'project'`). */
  scopeType?: string
  /** Filter by scope entity ID. */
  scopeId?: string
}

/**
 * Input for assigning a role to a user.
 */
export interface CreateRoleMembershipInput {
  /** ID of the role being assigned. */
  roleId: string
  /** ID of the user receiving the role. */
  userId: string
  /** User ID of the principal granting the role. */
  grantedBy: string
  /** ID of the owning organisation. */
  orgId: string
  /** Scope type inherited from the role. */
  scopeType?: string
  /** Scope entity ID inherited from the role. */
  scopeId?: string
  /** Optional expiry timestamp for time-limited memberships. */
  expiresAt?: string
}

/**
 * Operator for querying and mutating the `rolememberships` collection.
 *
 * Role memberships record that a user holds a particular role.
 * "Deletion" suspends the membership (`status: 'suspended'`) rather than
 * removing the document, preserving audit history.
 *
 * @example
 * ```typescript
 * const op = new RoleMembershipOperator(db)
 * const memberships = await op.query({ orgId: 'org_abc', userId: 'user_123' }).exec()
 * const m = await op.createMutation().execute({
 *   roleId: 'role_abc', userId: 'user_123', grantedBy: 'user_456', orgId: 'org_abc',
 * })
 * ```
 */
export class RoleMembershipOperator {
  constructor(private db: RxDatabase) {}

  /**
   * Build a query for role memberships with optional filters and pagination.
   *
   * Unlike `RoleOperator.query`, this does not apply an automatic
   * `archived` filter — callers should pass `status: 'active'` explicitly
   * when they want only live memberships.
   *
   * @param filters - Field filters to apply.
   * @param options - Pagination and sort options.
   * @returns A `QueryOperator` ready to `.exec()` or `.observable`.
   */
  query(
    filters: RoleMembershipFilters,
    options?: { sort?: string; limit?: number; skip?: number }
  ): QueryOperator<RoleMembershipModel> {
    const collection = this.db.collections.rolememberships
    const selector: Record<string, unknown> = {}

    if (filters.orgId) selector.orgId = { $eq: filters.orgId }
    if (filters.userId) selector.userId = { $eq: filters.userId }
    if (filters.roleId) selector.roleId = { $eq: filters.roleId }
    if (filters.status) selector.status = { $eq: filters.status }
    if (filters.scopeType) selector.scopeType = { $eq: filters.scopeType }
    if (filters.scopeId) selector.scopeId = { $eq: filters.scopeId }

    const queryOptions: QueryOptions<RoleMembershipModel> = {
      selector,
      sort: options?.sort ? [{ [options.sort]: 'desc' }] : [{ createdAt: 'desc' }],
      limit: options?.limit,
      skip: options?.skip,
    }

    return new QueryOperator<RoleMembershipModel>(collection, queryOptions)
  }

  /**
   * Fetch a single membership by its ID.
   *
   * @param id - The membership document ID.
   * @returns The membership as a plain object, or null if not found.
   */
  async findOne(id: string): Promise<RoleMembershipModel | null> {
    const doc = await this.db.collections.rolememberships.findOne(id).exec()
    return doc ? (doc.toJSON() as RoleMembershipModel) : null
  }

  /**
   * Create a mutation operator for inserting a new role membership.
   *
   * Assigns a generated ID, sets `status: 'active'`, and stamps timestamps.
   *
   * @returns A `MutationOperator` whose `execute()` resolves to the new membership.
   */
  createMutation(): MutationOperator<RoleMembershipModel, CreateRoleMembershipInput> {
    return new MutationOperator<RoleMembershipModel, CreateRoleMembershipInput>(
      this.db.collections.rolememberships,
      {
        mutationFn: async (collection, input) => {
          const now = new Date().toISOString()
          const membership: RoleMembershipModel = {
            id: createId('rolememberships'),
            roleId: input.roleId,
            userId: input.userId,
            grantedBy: input.grantedBy,
            orgId: input.orgId,
            scopeType: input.scopeType ?? '',
            scopeId: input.scopeId ?? '',
            expiresAt: input.expiresAt ?? '',
            // New memberships are active by default. Callers who need a
            // pending approval flow should update status after creation.
            status: 'active',
            createdAt: now,
            updatedAt: now,
          }
          await collection.insert(membership)
          return membership
        },
      }
    )
  }

  /**
   * Create a mutation operator for soft-removing a role membership.
   *
   * Sets `status: 'suspended'` rather than deleting the document so that
   * audit trails remain intact.
   *
   * @returns A `MutationOperator` whose `execute()` takes the membership ID.
   */
  deleteMutation(): MutationOperator<void, string> {
    return new MutationOperator<void, string>(this.db.collections.rolememberships, {
      mutationFn: async (collection, id) => {
        const doc = await collection.findOne(id).exec()
        if (!doc) throw new Error(`RoleMembership ${id} not found`)
        await doc.update({ $set: { status: 'suspended', updatedAt: new Date().toISOString() } })
      },
    })
  }
}
